'use client';

import Image from 'next/image';
import Link from 'next/link';
import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface PosterCardProps {
  /** Destination link. */
  href: string;
  /** Image URL, or null to show the placeholder. */
  imageUrl?: string | null;
  /** Main title shown below the poster. */
  title: string;
  /** Optional secondary line below the title (and inside the hover overlay). */
  subtitle?: string;
  /** Icon rendered when there is no image. */
  placeholderIcon: ReactNode;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const SIZE_CLASSES: Record<NonNullable<PosterCardProps['size']>, string> = {
  small: 'w-32 sm:w-40',
  large: 'w-48 sm:w-56 md:w-64',
  medium: 'w-40 sm:w-48 md:w-52',
};

/**
 * Shared poster-style card (2:3 aspect) used for people and users.
 * Keeps the dark + red identity consistent across the app.
 */
export function PosterCard({
  href,
  imageUrl,
  title,
  subtitle,
  placeholderIcon,
  size = 'medium',
  className = '',
}: PosterCardProps) {
  return (
    <Link href={href} className={cn('group block', SIZE_CLASSES[size], className)}>
      {/* Poster with 2:3 aspect ratio */}
      <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-gray-800 shadow-lg transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl group-hover:shadow-red-600/20">
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={title}
            fill
            className="object-cover"
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
            {placeholderIcon}
          </div>
        )}

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {subtitle && (
            <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
              <span className="text-xs sm:text-sm text-red-400 font-semibold">{subtitle}</span>
            </div>
          )}
        </div>
      </div>

      {/* Title */}
      <div className="mt-2 px-1">
        <h3 className="text-white font-medium text-sm sm:text-base truncate group-hover:text-red-400 transition-colors">
          {title}
        </h3>
        {subtitle && <p className="text-gray-400 text-xs sm:text-sm mt-0.5 truncate">{subtitle}</p>}
      </div>
    </Link>
  );
}
